import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { isEmpty } from "@/utils/query-string";
import {
  Card,
  ComboboxInputValueChangeDetails,
  Flex,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { AppColors } from "@/styles/colors";
import { InputField } from "@/components/input-field/InputField";
import { ComboboxComponent } from "@/components/select/Combobox";
import {
  getExistingFinancialMovementDesc,
  getFinancialMovementDescDetail,
} from "@/services/financial-movement-desc.service";
import { onlyNumbers } from "@/utils/reg-expressions";
import { handleUpdateFinancialMovementsDescriptionHandler } from "../schema/handler-functions";
import { FinancialMovementDesc } from "../schema/financial-movements-desc.schema";
import { useRoles } from "@/hooks/useRoles";

export const FinancialMovementsDescUpdatePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();

  const { control, setValue } = useForm();

  const handleCancel = () => {
    navigate(AppRoutes.FINANCIAL_MOVEMENTS_DESC);
  };

  const [
    loadingUpdateFinancialMovementsDesc,
    setLoadingUpdateFinancialMovementsDesc,
  ] = useState<boolean>(false);

  const [
    loadingFinancialMovementDescDetail,
    setLoadingFinancialMovementDescDetail,
  ] = useState<boolean>(false);
  const watchedValues = useWatch({
    control,
  });

  const [financialMovementDescDetail, setFinancialMovementDescDetail] =
    useState<FinancialMovementDesc | undefined>();

  const [loadingExistingDescriptions, setLoadingExistingDescriptions] =
    useState<boolean>(false);

  const [existingDescriptions, setExistingDescriptions] = useState<string[]>(
    [],
  );

  const fetchExistingDescriptions = async () => {
    setLoadingExistingDescriptions(true);
    try {
      const response = await getExistingFinancialMovementDesc();

      setExistingDescriptions(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExistingDescriptions(false);
    }
  };

  const fetchFinancialMovementDescDetail = async (id: number) => {
    setLoadingFinancialMovementDescDetail(true);
    try {
      const response = await getFinancialMovementDescDetail({ Codigo: id });

      setFinancialMovementDescDetail(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingFinancialMovementDescDetail(false);
    }
  };

  useEffect(() => {
    if (isActive && id) {
      fetchFinancialMovementDescDetail(Number(id));
      fetchExistingDescriptions();
    }
  }, [isActive]);

  useEffect(() => {
    if (financialMovementDescDetail) {
      setValue("code", financialMovementDescDetail?.nroComprobante);
      setDescriptionValue(financialMovementDescDetail?.descripcion);
    }
  }, [financialMovementDescDetail]);

  const [descriptionValue, setDescriptionValue] = useState<string>();

  const handleDescriptionChange = (e: ComboboxInputValueChangeDetails) => {
    setDescriptionValue(e.inputValue);
  };

  const hasNoFilledValues = useMemo(() => {
    const { code } = watchedValues || {};

    return isEmpty(descriptionValue) || isEmpty(code);
  }, [watchedValues, descriptionValue]);

  const { fetchPermissions } = useRoles();

  const handleUpdateFinancialMovementDescription = () => {
    handleUpdateFinancialMovementsDescriptionHandler({
      setLoadingUpdateFinancialMovementsDesc,
      navigate,
      watchedValues: {
        nombre: descriptionValue,
        codigo: watchedValues?.code,
      },
      fetchPermissions,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Editar relación comprobante"
        addButtonLabel="Confirmar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleUpdateFinancialMovementDescription}
        addButtonType="submit"
        disableAdd={
          hasNoFilledValues ||
          loadingUpdateFinancialMovementsDesc ||
          loadingFinancialMovementDescDetail
        }
        loading={
          loadingUpdateFinancialMovementsDesc ||
          loadingFinancialMovementDescDetail
        }
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                inputName="code"
                labelText="Código"
                control={control}
                placeholder="Ingresar código"
                allowedPattern={onlyNumbers}
                disabled
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              {Boolean(
                !loadingExistingDescriptions && existingDescriptions?.length,
              ) && (
                <ComboboxComponent
                  control={control}
                  name="name"
                  label="Nomenclatura"
                  inputValue={descriptionValue}
                  items={existingDescriptions.map((el: string) => ({
                    value: el,
                    label: el,
                  }))}
                  placeholder="Ingresar nomenclatura"
                  disabled={
                    loadingUpdateFinancialMovementsDesc ||
                    loadingExistingDescriptions
                  }
                  loading={loadingExistingDescriptions}
                  onChange={handleDescriptionChange}
                  clearOutside={false}
                />
              )}
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};
